import { RobotOutlined, SettingOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { PushSenderCollection } from '@gooduncles/gu-app-schema';
import { Button, Popover, Spin } from 'antd';
import { CSSProperties, FC, ReactNode, useState } from 'react';

import useSWRStore from 'src/hooks/swr/useSWRStore';
import useSWRUser from 'src/hooks/swr/useSWRUser';

const ROLE_LABEL: Record<string, string> = {
  admin: '관리자',
  manager: '매니저',
  user: '일반',
  unknown: '미확인',
};

const COLLECTION_META: Record<PushSenderCollection, { label: string; icon: ReactNode }> = {
  user: { label: '사용자', icon: <UserOutlined /> },
  store: { label: '매장', icon: <ShopOutlined /> },
  system: { label: '시스템', icon: <SettingOutlined /> },
  ai: { label: 'AI', icon: <RobotOutlined /> },
};

const subInfoStyle: CSSProperties = { fontSize: 12, color: '#595959' };
const idStyle: CSSProperties = { fontSize: 11, color: '#bfbfbf', marginTop: 4 };
const titleStyle: CSSProperties = { fontWeight: 600, marginBottom: 4 };
const missingStyle: CSSProperties = { color: '#8c8c8c' };

const UserContent: FC<{ id: string; enabled: boolean }> = ({ id, enabled }) => {
  const { user, isLoading } = useSWRUser(enabled ? id : null);

  if (isLoading) return <Spin size='small' />;
  if (!user) return <div style={missingStyle}>(사용자를 찾을 수 없음)</div>;

  const displayName = user.userName || user.email || '(이름 없음)';
  return (
    <div style={{ minWidth: 200 }}>
      <div style={titleStyle}>{displayName}</div>
      <div style={subInfoStyle}>역할: {ROLE_LABEL[user.role] ?? user.role}</div>
      {user.email && user.email !== displayName && <div style={subInfoStyle}>{user.email}</div>}
      <div style={idStyle}>{id}</div>
    </div>
  );
};

const StoreContent: FC<{ id: string; enabled: boolean }> = ({ id, enabled }) => {
  const { store, isLoading } = useSWRStore(enabled ? id : null);

  if (isLoading) return <Spin size='small' />;
  if (!store) return <div style={missingStyle}>(매장을 찾을 수 없음)</div>;

  return (
    <div style={{ minWidth: 200 }}>
      <div style={titleStyle}>{store.storeName}</div>
      {store.storeNickname && store.storeNickname !== store.storeName && (
        <div style={subInfoStyle}>{store.storeNickname}</div>
      )}
      <div style={idStyle}>{id}</div>
    </div>
  );
};

const StaticContent: FC<{ label: string; id: string }> = ({ label, id }) => (
  <div style={{ minWidth: 160 }}>
    <div style={titleStyle}>{label}</div>
    <div style={{ fontSize: 11, color: '#bfbfbf' }}>{id}</div>
  </div>
);

interface SenderInfoPopoverProps {
  label: string;
  collection: PushSenderCollection;
  id: string;
}

function renderContent(collection: PushSenderCollection, id: string, open: boolean, label: string): ReactNode {
  switch (collection) {
    case 'user':
      return <UserContent id={id} enabled={open} />;
    case 'store':
      return <StoreContent id={id} enabled={open} />;
    case 'system':
    case 'ai':
      return <StaticContent label={label} id={id} />;
  }
}

const SenderInfoPopover: FC<SenderInfoPopoverProps> = ({ label, collection, id }) => {
  const [open, setOpen] = useState(false);
  const meta = COLLECTION_META[collection];

  return (
    <Popover
      content={renderContent(collection, id, open, meta.label)}
      trigger='click'
      open={open}
      onOpenChange={setOpen}
    >
      <Button type='text' size='small' icon={meta.icon}>
        {label}
      </Button>
    </Popover>
  );
};

export default SenderInfoPopover;
